
import React, { Suspense, lazy } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import AlertBanner from "./components/AlertBanner";
import CookieConsent from "./components/CookieConsent";
import SkipNavigation from "./components/SkipNavigation";
import { AdminAuthProvider } from "./hooks/use-admin-auth";
import { DonationModalProvider } from "./components/donate/DonationModalContext";
import AdminLayout from "./components/admin/AdminLayout";



// Lazy load all page components
const Index = lazy(() => import("./pages/Index"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ProgramsPage = lazy(() => import("./pages/ProgramsPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const GetInvolvedPage = lazy(() => import("./pages/GetInvolvedPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const ResetPasswordPage = lazy(() => import("./pages/admin/ResetPasswordPage"));
const UpdatePasswordPage = lazy(() => import("./pages/admin/UpdatePasswordPage"));
const DashboardPage = lazy(() => import("./pages/admin/DashboardPage"));
const ContactSubmissionsPage = lazy(() => import("./pages/admin/ContactSubmissionsPage"));
const AdminEventsPage = lazy(() => import("./pages/admin/EventsPage"));
const VolunteersPage = lazy(() => import("./pages/admin/VolunteersPage"));
const DonorImportPage = lazy(() => import("./pages/admin/DonorImportPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/AnalyticsPage"));
const ManageDonationPage = lazy(() => import("./pages/ManageDonationPage"));
const EventDetailsPage = lazy(() => import("./pages/EventDetailsPage"));
const ProgramDetailsPage = lazy(() => import("./pages/ProgramDetailsPage"));
const AdminProgramsPage = lazy(() => import("./pages/admin/ProgramsPage"));
const TenCentsPage = lazy(() => import("./pages/TenCentsPage"));
const DonateSuccessPage = lazy(() => import("./pages/DonateSuccessPage"));
const DonateGeneralSuccessPage = lazy(() => import("./pages/DonateGeneralSuccessPage"));

const PageLoader = () => {
  const [showWarning, setShowWarning] = React.useState(false);
  const [dots, setDots] = React.useState('');

  React.useEffect(() => {
    const warningTimer = setTimeout(() => {
      setShowWarning(true);
    }, 5000);

    const dotsTimer = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    return () => {
      clearTimeout(warningTimer);
      clearInterval(dotsTimer);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center p-4">
      <div className="text-center max-w-md mx-auto">
        <div className="relative mb-6">
          <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto"></div>
          <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-r-secondary rounded-full animate-spin mx-auto mt-2 ml-2" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}></div>
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">
          Prince Hall Community Service Network
        </h2>
        <p className="text-muted-foreground mb-4">
          Loading application{dots}
        </p>
        {showWarning && (
          <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
            <p className="mb-2">Taking longer than expected...</p>
            <button 
              onClick={() => window.location.reload()} 
              className="text-amber-700 underline hover:no-underline"
            >
              Try refreshing the page
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Error Boundary for handling lazy loading errors
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): { hasError: boolean } {
    console.error('🚨 App ErrorBoundary caught error:', error);
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('🚨 App ErrorBoundary componentDidCatch:', error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center p-4">
          <div className="text-center max-w-lg mx-auto bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-3">
                Prince Hall Community Service Network
              </h1>
              <p className="text-gray-600 mb-6 leading-relaxed">
                We're experiencing technical difficulties. Our team has been notified and is working to resolve the issue.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors font-medium"
                >
                  Refresh Page
                </button>
                <button
                  onClick={() => window.history.back()}
                  className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Go Back
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                Error ID: {Date.now().toString(36)}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const App = () => {
  console.log('🏗️ App component initializing...');
  
  // Production-optimized QueryClient
  const queryClient = React.useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors
          if (error && typeof error === 'object' && 'status' in error) {
            const status = (error as any).status;
            if (status >= 400 && status < 500) return false;
          }
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      },
      mutations: {
        retry: 1,
      },
    },
  }), []);
  
  console.log('✅ QueryClient created');
  
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <DonationModalProvider>
            <AdminAuthProvider>
              <SkipNavigation />
              <Toaster />
              <Sonner />
              {/* <AlertBanner message="Congratulations to the 2025 PHCSN Student Aid Stipend Recipients!" /> */}
              <CookieConsent />
              <div className="flex flex-col min-h-screen" data-testid="app">
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Index />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/programs" element={<ProgramsPage />} />
                      <Route path="/events" element={<EventsPage />} />
                      <Route path="/get-involved" element={<GetInvolvedPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/admin/login" element={<AdminLoginPage />} />
                      <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
                      <Route path="/admin/update-password" element={<UpdatePasswordPage />} />
                      <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<DashboardPage />} />
                        <Route path="messages" element={<ContactSubmissionsPage />} />
                        <Route path="events" element={<AdminEventsPage />} />
                        <Route path="volunteers" element={<VolunteersPage />} />
                        <Route path="analytics" element={<AnalyticsPage />} />
                        <Route path="donor-import" element={<DonorImportPage />} />
                        <Route path="programs" element={<AdminProgramsPage />} />
                      </Route>
                      <Route path="/events/:id" element={<EventDetailsPage />} />
                      <Route path="/programs/:id" element={<ProgramDetailsPage />} />
                      <Route path="/10cents" element={<TenCentsPage />} />
                      <Route path="/10cents/success" element={<DonateSuccessPage />} />
                      <Route path="/donate/success" element={<DonateGeneralSuccessPage />} />
                      <Route path="/manage-donation" element={<ManageDonationPage />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </div>
            </AdminAuthProvider>
            </DonationModalProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
